import { useCallback, useEffect, useState } from "react";
import {
  deleteGitHubInstallation,
  GitHubApiError,
  linkGitHubRepo,
  listGitHubInstallations,
  listGitHubRepos,
  unlinkGitHubRepo,
  type GitHubInstallationView,
  type GitHubLinkedRepo,
  type GitHubRepoPickerItem,
} from "../lib/api";

/**
 * GitHub integration panel — shown on the Account page.
 *
 * Renders one card per installation (you can install the app on multiple
 * orgs or your personal account). For each installation we show:
 *   - account login + type + permission summary
 *   - linked repos (with unlink button)
 *   - "Link a repo" button that opens the picker dialog
 *   - "Remove from UAIP" (local delete — you uninstall on github.com separately)
 *
 * If there are no installations yet, we show a "Connect GitHub" CTA that
 * points at GET /api/github/install.
 *
 * Gotcha: when GitHub returns a 409 GITHUB_REINSTALL_REQUIRED we surface a
 * banner asking the user to re-connect — that's the signal the installation
 * was revoked server-side and our cached token is useless.
 */
export function GitHubIntegrationPanel(): JSX.Element {
  const [installations, setInstallations] = useState<GitHubInstallationView[] | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [reinstallFor, setReinstallFor] = useState<string | null>(null);
  const [pickerInstallation, setPickerInstallation] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const list = await listGitHubInstallations();
      setInstallations(list);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Refresh on return from `/api/github/callback?github=installed` — this
  // mirrors the `?checkout=success` pattern on billing.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("github") === "installed") {
      void refresh();
    }
  }, [refresh]);

  async function handleConnect(): Promise<void> {
    window.location.assign("/api/github/install");
  }

  async function handleRemove(installationId: string): Promise<void> {
    if (
      !window.confirm(
        "Remove this installation from UAIP? You can still uninstall the GitHub App on github.com.",
      )
    ) {
      return;
    }
    try {
      const res = await deleteGitHubInstallation(installationId);
      await refresh();
      if (res.revokeUrl) {
        window.open(res.revokeUrl, "_blank", "noopener");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  async function handleUnlink(linkedId: string): Promise<void> {
    try {
      await unlinkGitHubRepo(linkedId);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  }

  if (installations === null) {
    return (
      <section className="rounded-xl border border-slate-800 bg-slate-900/60 p-5">
        <header className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-medium">GitHub integration</h2>
        </header>
        <p className="text-sm text-slate-500">Loading installations…</p>
      </section>
    );
  }

  return (
    <section className="rounded-xl border border-slate-800 bg-slate-900/60 p-5">
      <header className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-medium">GitHub integration</h2>
          <p className="mt-0.5 text-xs text-slate-500">
            Link repos so you can send generated tests as a PR in one click.
          </p>
        </div>
        <button
          type="button"
          onClick={handleConnect}
          className="rounded-md bg-slate-800 px-3 py-1.5 text-sm text-slate-100 hover:bg-slate-700 border border-slate-700"
        >
          {installations.length === 0 ? "Connect GitHub" : "Add installation"}
        </button>
      </header>

      {error && (
        <div className="mb-3 rounded-md border border-rose-800/60 bg-rose-950/50 px-3 py-2 text-xs text-rose-200">
          {error}
        </div>
      )}

      {reinstallFor && (
        <div className="mb-3 rounded-md border border-amber-800/60 bg-amber-950/40 px-3 py-2 text-xs text-amber-200">
          GitHub rejected our installation token for{" "}
          <span className="font-medium">{reinstallFor}</span>. Please re-connect
          the app.{" "}
          <button
            type="button"
            onClick={handleConnect}
            className="underline decoration-dotted"
          >
            Re-connect
          </button>
        </div>
      )}

      {installations.length === 0 ? (
        <p className="text-sm text-slate-500">
          No GitHub installations yet. Click <b>Connect GitHub</b> above to
          install the UAIP app on a repo or organization.
        </p>
      ) : (
        <ul className="space-y-3">
          {installations.map((inst) => (
            <li
              key={inst.installationId}
              className="rounded-lg border border-slate-800 bg-slate-950/50 p-4"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-slate-100">
                      {inst.accountLogin}
                    </span>
                    <span className="rounded-full bg-slate-800 px-2 py-0.5 text-[10px] uppercase tracking-wide text-slate-400 border border-slate-700">
                      {inst.accountType}
                    </span>
                    <span className="rounded-full bg-slate-800 px-2 py-0.5 text-[10px] text-slate-400 border border-slate-700">
                      {inst.repositorySelection === "all"
                        ? "all repos"
                        : "selected repos"}
                    </span>
                  </div>
                  {inst.permissions && (
                    <div className="mt-1 text-[11px] text-slate-500">
                      {inst.permissions.replace(/,/g, " · ")}
                    </div>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPickerInstallation(inst.installationId)}
                    className="rounded-md border border-slate-700 bg-slate-800 px-2.5 py-1 text-xs text-slate-200 hover:bg-slate-700"
                  >
                    Link a repo
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRemove(inst.installationId)}
                    className="rounded-md border border-rose-800 px-2.5 py-1 text-xs text-rose-200 hover:bg-rose-950"
                  >
                    Remove
                  </button>
                </div>
              </div>
              {inst.linkedRepos.length > 0 && (
                <ul className="mt-3 divide-y divide-slate-800 rounded-md border border-slate-800">
                  {inst.linkedRepos.map((r) => (
                    <LinkedRepoRow key={r.id} repo={r} onUnlink={handleUnlink} />
                  ))}
                </ul>
              )}
              {inst.linkedRepos.length === 0 && (
                <p className="mt-3 text-xs text-slate-500">
                  No linked repos yet — link one to send PRs into it.
                </p>
              )}
            </li>
          ))}
        </ul>
      )}

      {pickerInstallation && (
        <RepoPickerDialog
          installationId={pickerInstallation}
          onClose={() => setPickerInstallation(null)}
          onLinked={async () => {
            await refresh();
            setPickerInstallation(null);
          }}
          onReinstallNeeded={() => {
            const hit = installations.find(
              (i) => i.installationId === pickerInstallation,
            );
            setReinstallFor(hit?.accountLogin ?? pickerInstallation);
            setPickerInstallation(null);
          }}
        />
      )}
    </section>
  );
}

function LinkedRepoRow(props: {
  repo: GitHubLinkedRepo;
  onUnlink: (id: string) => Promise<void>;
}): JSX.Element {
  const { repo, onUnlink } = props;
  return (
    <li className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
      <div className="min-w-0">
        <div className="truncate font-mono text-slate-200">{repo.fullName}</div>
        <div className="mt-0.5 text-[11px] text-slate-500">
          default branch <span className="text-slate-300">{repo.defaultBranch}</span>
          {repo.privateRepo && <span className="ml-2">· private</span>}
          {repo.lastPrUrl && (
            <>
              {" · "}
              <a
                className="text-indigo-300 hover:underline"
                href={repo.lastPrUrl}
                target="_blank"
                rel="noopener"
              >
                last PR
              </a>
            </>
          )}
        </div>
      </div>
      <button
        type="button"
        onClick={() => onUnlink(repo.id)}
        className="shrink-0 rounded-md border border-slate-700 px-2 py-0.5 text-xs text-slate-300 hover:bg-slate-800"
      >
        Unlink
      </button>
    </li>
  );
}

function RepoPickerDialog(props: {
  installationId: string;
  onClose: () => void;
  onLinked: () => Promise<void>;
  onReinstallNeeded: () => void;
}): JSX.Element {
  const { installationId, onClose, onLinked, onReinstallNeeded } = props;
  const [repos, setRepos] = useState<GitHubRepoPickerItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [linking, setLinking] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    listGitHubRepos(installationId, 1, 100)
      .then((r) => {
        if (!cancelled) setRepos(r.repositories);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof GitHubApiError && err.code === "GITHUB_REINSTALL_REQUIRED") {
          onReinstallNeeded();
          return;
        }
        setError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
  }, [installationId, onReinstallNeeded]);

  async function handleLink(repo: GitHubRepoPickerItem): Promise<void> {
    setError(null);
    setLinking(repo.repoId);
    try {
      await linkGitHubRepo(installationId, {
        repoId: repo.repoId,
        owner: repo.owner,
        name: repo.name,
        fullName: repo.fullName,
        defaultBranch: repo.defaultBranch,
        privateRepo: repo.private,
      });
      await onLinked();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setLinking(null);
    }
  }

  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/70"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-xl border border-slate-800 bg-slate-950 p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="mb-3 flex items-center justify-between">
          <h3 className="text-base font-medium text-slate-100">Link a repo</h3>
          <button
            type="button"
            onClick={onClose}
            className="text-xs text-slate-500 hover:text-slate-300"
          >
            Close
          </button>
        </header>
        {error && (
          <div className="mb-3 rounded-md border border-rose-800/60 bg-rose-950/50 px-3 py-2 text-xs text-rose-200">
            {error}
          </div>
        )}
        {repos === null ? (
          <p className="text-sm text-slate-500">Loading repos…</p>
        ) : repos.length === 0 ? (
          <p className="text-sm text-slate-500">
            This installation can't see any repositories yet — grant access to at
            least one repo on github.com and refresh.
          </p>
        ) : (
          <ul className="max-h-[60vh] divide-y divide-slate-800 overflow-auto rounded-md border border-slate-800">
            {repos.map((r) => (
              <li
                key={r.repoId}
                className="flex items-center justify-between px-3 py-2 text-sm"
              >
                <div className="min-w-0">
                  <div className="truncate font-mono text-slate-200">{r.fullName}</div>
                  <div className="text-[11px] text-slate-500">
                    {r.private ? "private" : "public"} · default {r.defaultBranch}
                  </div>
                </div>
                <button
                  type="button"
                  disabled={linking === r.repoId}
                  onClick={() => handleLink(r)}
                  className="ml-3 shrink-0 rounded-md bg-indigo-500 px-2.5 py-1 text-xs text-white hover:bg-indigo-400 disabled:opacity-50"
                >
                  {linking === r.repoId ? "Linking…" : "Link"}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
